import { FormGroup } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';
import {
  buildCompareColumnDefinitions,
  buildSelectedColumnsClipboardText,
  computeHeaderSelection,
  isHeaderSelected as isHeaderSelectedHelper
} from './units-grid-input-state.helper';
import {
  createCompareContextMenuItems,
  isDepthColumn,
  openCompareDialog
} from '../ng-table-grid/compare-dialog.helpers';
import { buildUnitsGridCompareData, buildUnitsGridRawRows } from './units-grid-input-row-ops.helper';
import { UnitsGridColumn } from './units-grid-input.models';
import { MenuItem } from 'primeng/api';

interface SelectedCellState {
  rowIndex: number;
  columnIndex: number;
}

export interface UnitsGridViewActionsCoordinatorConfig {
  getColumns: () => readonly UnitsGridColumn[];
  getRows: () => FormGroup[];
  getHeader: (column: UnitsGridColumn) => string;
  enableColumnMultiSelect: () => boolean;
  enableCompareValues: () => boolean;
  enableContextMenu: () => boolean;
  showGridContextMenu: (event: MouseEvent) => void;
  showCompareContextMenu: (event: MouseEvent) => void;
}

export class UnitsGridViewActionsCoordinator {
  private selectedColumns: string[] = [];
  private selectedCell: SelectedCellState = { rowIndex: 0, columnIndex: 0 };
  private compareColumn: UnitsGridColumn | null = null;

  public readonly compareContextMenuItems: MenuItem[];

  constructor(
    private readonly dialogService: DialogService,
    private readonly config: UnitsGridViewActionsCoordinatorConfig
  ) {
    this.compareContextMenuItems = createCompareContextMenuItems(() => this.openCompareValuesDialog());
  }

  public onHeaderClick(event: MouseEvent, column: UnitsGridColumn): void {
    if (!this.config.enableColumnMultiSelect()) {
      return;
    }

    const isMultiSelectClick = event.ctrlKey || event.metaKey;
    if (!isMultiSelectClick && this.selectedColumns.length === 1 && this.selectedColumns[0] === column.field) {
      this.selectedColumns = [];
      return;
    }

    this.selectedColumns = computeHeaderSelection(
      this.selectedColumns,
      column.field,
      isMultiSelectClick
    );
  }

  public onHeaderContextMenu(event: MouseEvent, column: UnitsGridColumn): void {
    if (!this.config.enableCompareValues()) {
      return;
    }

    if (isDepthColumn(column.field, this.getCompareColumnDefinitions())) {
      return;
    }

    event.preventDefault();
    this.compareColumn = column;
    this.config.showCompareContextMenu(event);
  }

  public isHeaderSelected(columnField: string): boolean {
    return isHeaderSelectedHelper(this.selectedColumns, columnField);
  }

  public onCellClick(): void {
    if (this.selectedColumns.length > 0) {
      this.selectedColumns = [];
    }
  }

  public onCellContextMenu(event: MouseEvent, rowIndex: number, columnIndex: number): void {
    if (!this.config.enableContextMenu()) {
      return;
    }

    event.preventDefault();
    this.selectedCell = { rowIndex, columnIndex };
    this.config.showGridContextMenu(event);
  }

  public getSelectedCell(): SelectedCellState {
    return this.selectedCell;
  }

  public buildSelectedColumnsText(): string {
    return buildSelectedColumnsClipboardText({
      columns: this.config.getColumns(),
      selectedColumns: this.selectedColumns,
      rows: buildUnitsGridRawRows(this.config.getRows()),
      getHeader: (column) => this.config.getHeader(column)
    });
  }

  private openCompareValuesDialog(): void {
    if (!this.compareColumn) {
      return;
    }

    const gridData = buildUnitsGridCompareData(this.config.getRows(), this.config.getColumns());

    openCompareDialog(
      this.dialogService,
      { field: this.compareColumn.field, header: this.config.getHeader(this.compareColumn) },
      this.getCompareColumnDefinitions(),
      gridData
    );
  }

  private getCompareColumnDefinitions(): Array<{ field: string; header: string }> {
    return buildCompareColumnDefinitions(this.config.getColumns(), (column) => this.config.getHeader(column));
  }
}
